import { useState, useEffect, useMemo, useCallback } from "react";
import {
  KYCStatus,
  RegtankReattemptApprovalEnum,
} from "@funded-here-interface/common/src/constant/enum";
import useDebounce from "@funded-here-interface/common/src/hooks/useDebounce";
import useGetOrgsMutation from "./useGetOrgs";
import { getRegtankReattemptStatus } from "./useRegtankReattemptStatus";
import { UserData } from "../types/org.type";

// Constants
const FILTER_ALL = "ALL" as const;
const DEBOUNCE_DELAY = 1000;

interface Organization {
  user: UserData[];
  [key: string]: any;
}

interface EnrichedOrganization extends Organization {
  regtankReattemptApproval: RegtankReattemptApprovalEnum;
}

interface UseRegtankFilteredListOptions {
  token: string;
  isIndividual: boolean;
  includeResubmission?: boolean;
}

export const useOrgIndividualsFilteredList = (
  options: UseRegtankFilteredListOptions
) => {
  const { token, isIndividual, includeResubmission = false } = options;

  // State management - keep strings for backward compatibility
  const [page, setPage] = useState("1");
  const [limit, setLimit] = useState("10");
  const [kycStatus, setKycStatus] = useState<string>(FILTER_ALL);
  const [search, setSearch] = useState("");
  const [regtankReattemptApproval, setRegtankReattemptApproval] =
    useState<string>(FILTER_ALL);

  // Memoized filter options
  const limits = useMemo(() => ["10", "20", "50"], []);

  const kycStatusFilter = useMemo(
    () => [
      FILTER_ALL,
      KYCStatus.APPROVED.toUpperCase(),
      KYCStatus.PENDING_APPROVAL.toUpperCase(),
      KYCStatus.REJECTED.toUpperCase(),
      ...(includeResubmission
        ? [KYCStatus.PENDING_RESUBMISSION.toUpperCase()]
        : []),
    ],
    [includeResubmission]
  );

  const regtankReattemptApprovalFilter = useMemo(
    () => [
      FILTER_ALL,
      RegtankReattemptApprovalEnum.ACTION_REQUIRED.toUpperCase(),
      RegtankReattemptApprovalEnum.NO_ACTION_REQUIRED.toUpperCase(),
    ],
    []
  );

  // Search and data fetching
  const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY);
  const { data, error, isLoading, refetch } = useGetOrgsMutation(
    token,
    limit,
    page,
    isIndividual,
    kycStatus !== FILTER_ALL ? kycStatus.toLowerCase() : "",
    debouncedSearch
  );

  // Get organizations from API data
  const organizations = useMemo<Organization[]>(
    () => data?.organizations || [],
    [data]
  );

  const totalCount = Number(
    useMemo<string>(() => data?.totalCount || 0, [data])
  );

  // Step 1: Filter by Regtank status (before enrichment for performance)
  // Only enrich organizations that pass initial filtering
  const filteredOrgIndices = useMemo(() => {
    if (regtankReattemptApproval === FILTER_ALL) {
      return organizations.map((_, idx) => idx);
    }
    return organizations.reduce<number[]>((indices, org, idx) => {
      const orgStatus = getRegtankReattemptStatus(org.user);
      if (orgStatus === regtankReattemptApproval) {
        indices.push(idx);
      }
      return indices;
    }, []);
  }, [organizations, regtankReattemptApproval]);

  // Step 2: Pagination indices
  const paginationRange = useMemo(() => {
    const limitNum = parseInt(limit, 10);
    const startIdx = 0;
    const endIdx = limitNum;
    return { startIdx, endIdx };
  }, [limit]);

  // Step 3: Enrich only visible organizations (performance optimization)
  const paginatedOrgs = useMemo<EnrichedOrganization[]>(() => {
    const visibleIndices = filteredOrgIndices.slice(
      paginationRange.startIdx,
      paginationRange.endIdx
    );

    return visibleIndices.map((orgIdx) => {
      const org = organizations[orgIdx];
      return {
        ...org,
        regtankReattemptApproval: getRegtankReattemptStatus(org.user),
      };
    });
  }, [filteredOrgIndices, paginationRange, organizations]);

  // All enriched organizations (for export or other uses)
  const enrichedOrgs = useMemo<EnrichedOrganization[]>(() => {
    return organizations.map((org) => ({
      ...org,
      regtankReattemptApproval: getRegtankReattemptStatus(org.user),
    }));
  }, [organizations]);

  // Filtered (but not paginated) enriched orgs
  const filteredOrgs = useMemo<EnrichedOrganization[]>(() => {
    if (regtankReattemptApproval === FILTER_ALL) {
      return enrichedOrgs;
    }
    return enrichedOrgs.filter(
      (org) => org.regtankReattemptApproval === regtankReattemptApproval
    );
  }, [enrichedOrgs, regtankReattemptApproval]);

  // Page count
  const pageCount = useMemo(
    () => Math.ceil(filteredOrgIndices.length / parseInt(limit, 10)),
    [filteredOrgIndices.length, limit]
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage("1");
  }, [debouncedSearch]);

  // Reusable handler to reset page when filter changes
  const resetToFirstPage = useCallback(() => setPage("1"), []);

  // Handlers - using useCallback for performance
  const handleSearchChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const searchedTerm = (e.target as HTMLTextAreaElement).value;
      setSearch(searchedTerm);
    },
    []
  );

  const handlePageChange = useCallback((event: { selected: number }) => {
    const pageNo = event.selected + 1;
    setPage(`${pageNo}`);
  }, []);

  const handleLimitChange = useCallback(
    (newLimit: string) => {
      resetToFirstPage();
      setLimit(newLimit);
    },
    [resetToFirstPage]
  );

  const handleKycStatusChange = useCallback(
    (value: string) => {
      resetToFirstPage();
      setKycStatus(value);
    },
    [resetToFirstPage]
  );

  const handleRegtankApprovalChange = useCallback(
    (value: string) => {
      resetToFirstPage();
      setRegtankReattemptApproval(value);
    },
    [resetToFirstPage]
  );

  return {
    // State
    page,
    setPage,
    limit,
    setLimit,
    kycStatus,
    search,
    regtankReattemptApproval,
    organizations,

    // Constants
    limits,
    kycStatusFilter,
    regtankReattemptApprovalFilter,

    // Data
    paginatedOrgs,
    filteredOrgs,
    enrichedOrgs,
    data,
    error,
    isLoading,
    refetch,

    // Handlers
    handleSearchChange,
    handlePageChange,
    handleLimitChange,
    handleKycStatusChange,
    handleRegtankApprovalChange,

    // Computed
    pageCount,
    totalCount,
  };
};
